import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Draggable } from '@hello-pangea/dnd';
import { onSnapshot, doc, setDoc, getDoc, updateDoc, writeBatch, addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import ProjectCard, { ViewType } from './ProjectCard';
import AddProjectModal from './AddProjectModal';
import CreateWorkspaceModal from './CreateWorkspaceModal';
import ProjectChecklistModal from './ProjectChecklistModal';
import ProjectDetailsModal from './ProjectDetailsModal';
import AddBookmarkModal from './bookmark/AddBookmarkModal';
import {
    updateProjectOrder,
    toggleProjectStarStatus,
    updateUserTodos,
    initializeUserProjectSettings
} from '../utils/firebaseUtils';
import {
    DashboardContainer,
    MainContent,
    MainWrapper,
    LinkButton,
    ToggleLinksContainer,
    FullscreenButton,
} from '../styles/Dashboard.styles';
import CollapseAllIcon from './icons/CollapseAllIcon';
import { filterProjects } from '../utils/projectFilteringUtils'; // Import the utility function
import DashboardClock from './dashboard/DashboardClock';
import DashboardProjects from './dashboard/DashboardProjects';
import DashboardHeader from './dashboard/DashboardHeader';
import DashboardSidebar from './dashboard/DashboardSidebar';
import BookmarksSection from './bookmark/BookmarksSection';
import SavesSection from './SavesSection';
import AddSaveModal from './AddSaveModal';
import ThinkingItemsModal from './ThinkingItemsModal';
import { useToasts } from '../hooks/useToasts';
import { useSaves } from '../hooks/useSaves';
import ToastContainer from './ToastContainer';
import FullscreenIcon from './icons/FullscreenIcon';
import GlobalStyle from '../styles/GlobalStyle';
import { useDashboardData } from '../hooks/useDashboardData';

function Dashboard() {
    const { user } = useAuth();
    const { toasts, showToast, hideToast } = useToasts();
    const { allUniqueTags, addSave, allSaves } = useSaves(user?.uid);
    const location = useLocation();
    const navigate = useNavigate();
    const {
        projects,
        projectOrder,
        setProjectOrder,
        isLoading,
        globalTodos,
        setGlobalTodos,
        starredProjectIds,
        workspaces,
        projectsByWorkspace,
        unassignedProjects,
        unassignedProjectOrder,
        setUnassignedProjectOrder
    } = useDashboardData(user);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchFilter, setSearchFilter] = useState('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [createWorkspaceModalOpen, setCreateWorkspaceModalOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
        // Try to get the sidebar state from sessionStorage
        const savedState = sessionStorage.getItem('dashboardSidebarOpen');
        return savedState === 'true';
    });
    
    // Save isSidebarOpen to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('dashboardSidebarOpen', isSidebarOpen.toString());
    }, [isSidebarOpen]);
    const [isMainContentOpen, setIsMainContentOpen] = useState(() => {
        // Try to get the main content state from sessionStorage
        const savedState = sessionStorage.getItem('dashboardMainContentOpen');
        return savedState === 'true';
    });
    
    // Save isMainContentOpen to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('dashboardMainContentOpen', isMainContentOpen.toString());
    }, [isMainContentOpen]);
    const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(null);
    const [workspaceOrder, setWorkspaceOrder] = useState(() => {
        try {
            const cached = window.localStorage.getItem('dashboardWorkspaceOrder');
            return cached ? JSON.parse(cached) : [];
        } catch {
            return [];
        }
    });
    const [isWorkspaceOrderLoading, setIsWorkspaceOrderLoading] = useState(() => {
        return workspaceOrder.length === 0;
    });
    const [dashboardBackground, setDashboardBackground] = useState(null);
    const [isAddBookmarkModalOpen, setIsAddBookmarkModalOpen] = useState(false);
    const [isAddSaveModalOpen, setIsAddSaveModalOpen] = useState(false);
    const [isThinkingModalOpen, setIsThinkingModalOpen] = useState(false);
    const [activeSection, setActiveSection] = useState(() => {
        // Try to get the active section from sessionStorage
        const savedSection = sessionStorage.getItem('activeDashboardSection');
        return savedSection || null;
    });

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    };
    
    // Save activeSection to sessionStorage whenever it changes
    useEffect(() => {
        if (activeSection) {
            sessionStorage.setItem('activeDashboardSection', activeSection);
        } else {
            sessionStorage.removeItem('activeDashboardSection');
        }
    }, [activeSection]);

    // Effect to control ThinkingItemsModal based on route
    useEffect(() => {
        if (location.pathname === '/ideas') {
            setIsThinkingModalOpen(true);
        } else {
            // If modal is open but path is not /ideas, close it
            // This handles browser back navigation or direct URL changes away from /ideas
            if (isThinkingModalOpen) {
                setIsThinkingModalOpen(false);
            }
        }
    }, [location.pathname, isThinkingModalOpen]);

    const handleCollapseAll = () => {
        setIsMainContentOpen(false);
        setActiveSection(null);
        setIsSidebarOpen(false);
    };

    useEffect(() => {
        if (!user) return;

        const initBackground = async () => {
            const userSettings = await initializeUserProjectSettings(user.uid);
            if (userSettings) {
                setDashboardBackground(userSettings.background);
            }
        };
        initBackground();

        // Listen for user settings for background
        const unsubscribeUserSettings = onSnapshot(
            doc(db, 'userSettings', user.uid),
            (doc) => {
                if (doc.exists()) {
                    setDashboardBackground(doc.data().background);
                }
            }
        );

        // Listen for workspace order changes
        const unsubscribeWorkspaceOrder = onSnapshot(
            doc(db, 'settings', 'workspaceOrder'),
            (snapshot) => {
                if (snapshot.exists()) {
                    const newOrder = snapshot.data().order || [];
                    if (JSON.stringify(newOrder) !== JSON.stringify(workspaceOrder)) {
                        setWorkspaceOrder(newOrder);
                        try {
                            window.localStorage.setItem('dashboardWorkspaceOrder', JSON.stringify(newOrder));
                        } catch {
                            // Ignore localStorage write errors
                        }
                    }
                } else {
                    // Initialize the document if it doesn't exist
                    setDoc(doc(db, 'settings', 'workspaceOrder'), {
                        order: [],
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString()
                    });
                }
                setIsWorkspaceOrderLoading(false);
            }
        );

        return () => {
            unsubscribeUserSettings();
            unsubscribeWorkspaceOrder();
        };
    }, [user, workspaceOrder]);

    const handleMoveProject = async (projectId, workspaceId) => {
        try {
            const batch = writeBatch(db);

            // Get the project to find its current workspace
            const projectRef = doc(db, 'projects', projectId);
            const projectSnap = await getDoc(projectRef);
            const currentWorkspaceId = projectSnap.data()?.workspaceId;

            // Update project
            batch.update(projectRef, {
                workspaceId: workspaceId,
                updatedAt: new Date().toISOString()
            });

            // If project was in a workspace, update old workspace
            if (currentWorkspaceId) {
                const currentWorkspaceRef = doc(db, 'workspaces', currentWorkspaceId);
                const currentWorkspaceSnap = await getDoc(currentWorkspaceRef);
                const currentOrder = currentWorkspaceSnap.data()?.projectOrder || [];

                batch.update(currentWorkspaceRef, {
                    projectOrder: currentOrder.filter(id => id !== projectId),
                    updatedAt: new Date().toISOString()
                });
            }

            // If moving to a new workspace, update new workspace
            if (workspaceId) {
                const newWorkspaceRef = doc(db, 'workspaces', workspaceId);
                const newWorkspaceSnap = await getDoc(newWorkspaceRef);
                const newOrder = newWorkspaceSnap.data()?.projectOrder || [];

                batch.update(newWorkspaceRef, {
                    projectOrder: [...newOrder, projectId],
                    updatedAt: new Date().toISOString()
                });
            }

            await batch.commit();
        } catch (error) {
            console.error('Error moving project:', error);
        }
    };

    const handleArchiveProject = async (projectId) => {
        try {
            const projectRef = doc(db, 'projects', projectId);
            await updateDoc(projectRef, {
                archived: true,
                archivedAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error archiving project:', error);
        }
    };

    const handleDuplicateProject = async (projectId) => {
        try {
            const projectRef = doc(db, 'projects', projectId);
            const projectDoc = await getDoc(projectRef);
            if (projectDoc.exists()) {
                const projectData = projectDoc.data();
                const newProject = {
                    ...projectData,
                    title: `Duplicate of ${projectData.title}`,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };
                delete newProject.id;
                await addDoc(collection(db, 'projects'), newProject);
            }
        } catch (error) {
            console.error('Error duplicating project:', error);
        }
    };

    const handleDragEnd = async (result) => {
        const { destination, source, draggableId, type } = result;

        if (!destination) return;

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        const projectId = draggableId.split('-')[0];

        if (type === 'WORKSPACE') {
            const newOrder = Array.from(workspaceOrder);
            const [removed] = newOrder.splice(source.index, 1);
            newOrder.splice(destination.index, 0, removed);

            setWorkspaceOrder(newOrder);

            // Update workspace order in Firebase
            await updateDoc(doc(db, 'settings', 'workspaceOrder'), {
                order: newOrder,
                updatedAt: new Date().toISOString()
            });
        } else if (type === 'PROJECT') {
            try {
                // Moving between different sections
                if (destination.droppableId !== source.droppableId) {
                    if (destination.droppableId.startsWith('workspace-')) {
                        const destWorkspaceId = destination.droppableId.replace('workspace-', '');
                        await handleMoveProject(projectId, destWorkspaceId);
                    } else if (destination.droppableId === 'unassigned-projects') {
                        await handleMoveProject(projectId, null);
                    }
                }

                // Handle reordering based on destination
                if (destination.droppableId === 'unassigned-projects') {
                    // Reorder unassigned projects
                    const newOrder = Array.from(unassignedProjectOrder);
                    const oldIndex = newOrder.indexOf(projectId);
                    if (oldIndex !== -1) {
                        newOrder.splice(oldIndex, 1);
                    }
                    newOrder.splice(destination.index, 0, projectId);

                    // Update unassigned order in Firebase
                    await updateDoc(doc(db, 'settings', 'unassignedProjectOrder'), {
                        order: newOrder,
                        updatedAt: new Date().toISOString()
                    });
                    setUnassignedProjectOrder(newOrder);
                } else if (source.droppableId.startsWith('workspace-') &&
                    source.droppableId === destination.droppableId) {
                    // Reorder within workspace
                    const workspaceId = source.droppableId.replace('workspace-', '');
                    const workspace = workspaces.find(w => w.id === workspaceId);
                    if (workspace) {
                        const currentProjects = [...(projectsByWorkspace[workspaceId] || [])];
                        const [removed] = currentProjects.splice(source.index, 1);
                        currentProjects.splice(destination.index, 0, removed);

                        await updateDoc(doc(db, 'workspaces', workspaceId), {
                            projectOrder: currentProjects.map(p => p.id),
                            updatedAt: new Date().toISOString()
                        });
                    }
                } else if (destination.droppableId === 'starred-projects') {
                    // Handle starred projects order
                    const newOrder = Array.from(projectOrder);
                    const oldIndex = newOrder.indexOf(projectId);
                    if (oldIndex !== -1) {
                        newOrder.splice(oldIndex, 1);
                    }
                    newOrder.splice(destination.index, 0, projectId);

                    const orderDocRef = doc(db, 'settings', 'projectOrder');
                    await updateProjectOrder(orderDocRef, newOrder);
                    setProjectOrder(newOrder);
                }
            } catch (error) {
                console.error('Error updating project order:', error);
            }
        }
    };

    const handleGlobalTodoOperations = {
        add: async (text, properties) => {
            const { addToEnd, ...todoProperties } = properties || {};
            const newTodo = {
                id: `todo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                text,
                completed: false,
                createdAt: new Date().toISOString(),
                ...todoProperties,
            };
            const newTodos = addToEnd ? [...globalTodos, newTodo] : [newTodo, ...globalTodos];
            setGlobalTodos(newTodos); // Optimistic update
            await updateUserTodos(user.uid, newTodos);
        },

        toggle: async (index) => {
            const newTodos = [...globalTodos];
            newTodos[index].completed = !newTodos[index].completed;
            setGlobalTodos(newTodos); // Optimistic update
            await updateUserTodos(user.uid, newTodos);
        },

        delete: async (index) => {
            const newTodos = [...globalTodos];
            newTodos.splice(index, 1);
            setGlobalTodos(newTodos); // Optimistic update
            await updateUserTodos(user.uid, newTodos);
        },

        update: async (index, updatedProperties) => {
            const newTodos = [...globalTodos];
            newTodos[index] = {
                ...newTodos[index],
                ...updatedProperties,
                updatedAt: new Date().toISOString()
            };
            setGlobalTodos(newTodos); // Optimistic update
            await updateUserTodos(user.uid, newTodos);
        },

        toggleFocus: async (index) => {
            const newTodos = [...globalTodos];
            const item = newTodos[index];
            item.focused = !item.focused;
            setGlobalTodos(newTodos); // Optimistic update
            await updateUserTodos(user.uid, newTodos);
        },

        toggleCompleteAndFocus: async (index) => {
            const newTodos = [...globalTodos];
            const item = newTodos[index];
            item.completed = !item.completed;
            item.focused = false;
            setGlobalTodos(newTodos); // Optimistic update
            await updateUserTodos(user.uid, newTodos);
        },

        reorder: async (result, reorderedSubset) => {
            const { destination, source } = result;
            if (!destination) {
                return;
            }
            if (destination.droppableId === source.droppableId && destination.index === source.index) {
                return;
            }

            const reorderedItems = Array.from(reorderedSubset);
            const [removed] = reorderedItems.splice(source.index, 1);
            reorderedItems.splice(destination.index, 0, removed);

            const reorderedIds = reorderedItems.map(item => item.id);
            const newTodos = [...globalTodos];

            newTodos.sort((a, b) => {
                const aIndex = reorderedIds.indexOf(a.id);
                const bIndex = reorderedIds.indexOf(b.id);

                if (aIndex > -1 && bIndex > -1) {
                    return aIndex - bIndex;
                }
                if (aIndex > -1) {
                    return -1;
                }
                if (bIndex > -1) {
                    return 1;
                }
                // For items not in the reordered list, maintain their relative order.
                // This can be implicitly handled by Array.prototype.sort's stability if we just return 0.
                // However, to be explicit, we can compare their original positions, but that's complex.
                // Let's rely on the fact that we only reorder focused items among themselves.
                return 0;
            });

            setGlobalTodos(newTodos); // Optimistic update
            await updateUserTodos(user.uid, newTodos);
        }
    };

    // Filter projects based on search query and filter before sorting
    // This uses the same utility function for consistency.
    const filteredProjectsForDisplay = filterProjects(projects, searchQuery, searchFilter);

    const sortedProjects = [...filteredProjectsForDisplay].sort((a, b) => {
        const aIndex = projectOrder.indexOf(a.id);
        const bIndex = projectOrder.indexOf(b.id);

        // If both projects are in the order array
        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }

        // If only one project is in the order array
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // If neither project is in the order array, sort by creation date
        return new Date(b.createdAt) - new Date(a.createdAt);
    });

    // Then filter for starred/non-starred
    const starredProjects = sortedProjects.filter(p => starredProjectIds.includes(p.id));

    const handleCardClick = (project, viewType) => {
        if (viewType === ViewType.BOOKMARK && project.url) {
            window.open(project.url, '_blank');
            setSelectedProject(null);
            setIsChecklistModalOpen(false);
            setShowDetailsModal(null);
        } else if (viewType === ViewType.TAGLINE) {
            setShowDetailsModal(project);
            setSelectedProject(null);
            setIsChecklistModalOpen(false);
        }
        else {
            setSelectedProject(project);
            setIsChecklistModalOpen(true);
            setShowDetailsModal(null);
        }
    };

    const renderProjectCard = (project, index, location) => {
        return (
            <Draggable
                key={project.uniqueId || project.id}
                draggableId={project.uniqueId || project.id}
                index={index}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.5 : 1
                        }}
                    >
                        <ProjectCard
                            project={project}
                            onToggleStar={() => toggleProjectStarStatus(
                                user.uid,
                                project.id,
                                starredProjectIds.includes(project.id)
                            )}
                            isStarred={starredProjectIds.includes(project.id)}
                            onArchive={handleArchiveProject}
                            onDuplicate={handleDuplicateProject}
                            onCardClick={handleCardClick}
                        />
                    </div>
                )}
            </Draggable>
        );
    };

    return (
        <DashboardContainer style={{
            background: dashboardBackground ? `url(${dashboardBackground?.urls?.full})` : 'url(https://images.unsplash.com/photo-1505235687559-28b5f54645b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w2ODE2MTB8MHwxfHJhbmRvbXx8fHx8fHx8fDE3MzcwMTEyMzR8&ixlib=rb-4.0.3&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            transition: 'background 0.3s ease-in-out',
            backgroundAttachment: 'fixed',
            minHeight: 'calc(100vh - 41px)',
            padding: '4px'
        }}>
            <GlobalStyle lockScroll={isChecklistModalOpen || showDetailsModal || isThinkingModalOpen} />
            <ToggleLinksContainer>
                <div>
                    <LinkButton onClick={handleCollapseAll} title="Collapse All">
                        <CollapseAllIcon />
                    </LinkButton>
                    <LinkButton onClick={() => setIsMainContentOpen(!isMainContentOpen)}>
                        Projects
                    </LinkButton>
                    <LinkButton onClick={() => setActiveSection(activeSection === 'bookmarks' ? null : 'bookmarks')}>
                        Bookmarks
                    </LinkButton>
                    <LinkButton onClick={() => setActiveSection(activeSection === 'saves' ? null : 'saves')}>
                        Saves
                    </LinkButton>
                </div>
                <div>
                    {/* Old version of Plan that had many unsolvable bugs */}
                    {/* TODO: completely remove it with all nested spaghetti logic */}
                    {/* <LinkButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                        Plan
                    </LinkButton> */}
                    <LinkButton onClick={() => navigate('/ideas')}>
                        Plan
                    </LinkButton>
                </div>
            </ToggleLinksContainer>
            {activeSection === 'bookmarks' && (
                <BookmarksSection userId={user.uid} />
            )}
            {activeSection === 'saves' && (
                <SavesSection showToast={showToast} />
            )}
            <ToastContainer toasts={toasts} onDismiss={hideToast} />
            {!isMainContentOpen && activeSection !== 'saves' && (
                <DashboardClock
                    user={user}
                    globalTodos={globalTodos}
                    handleGlobalTodoOperations={handleGlobalTodoOperations}
                />
            )}
            <MainWrapper>
                <MainContent isOpen={isMainContentOpen}>
                    <DashboardHeader
                        setIsModalOpen={setIsModalOpen}
                        setCreateWorkspaceModalOpen={setCreateWorkspaceModalOpen}
                        setIsSidebarOpen={setIsSidebarOpen}
                        setIsAddBookmarkModalOpen={setIsAddBookmarkModalOpen}
                        setIsAddSaveModalOpen={setIsAddSaveModalOpen}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        searchFilter={searchFilter}
                        setSearchFilter={setSearchFilter}
                    />
                    {isMainContentOpen && !isLoading && !isWorkspaceOrderLoading && (
                        <DashboardProjects
                            projects={projects}
                            searchQuery={searchQuery}
                            searchFilter={searchFilter}
                            // starredProjects are now derived from filteredProjectsForDisplay via sortedProjects
                            starredProjects={starredProjects}
                            workspaces={workspaces}
                            workspaceOrder={workspaceOrder}
                            projectsByWorkspace={projectsByWorkspace} // This is now based on filtered projects (excluding starred)
                            unassignedProjects={unassignedProjects} // This is now based on filtered projects (excluding starred)
                            projectOrder={projectOrder}
                            starredProjectIds={starredProjectIds}
                            handleMoveProject={handleMoveProject}
                            handleArchiveProject={handleArchiveProject}
                            handleDuplicateProject={handleDuplicateProject}
                            toggleProjectStarStatus={toggleProjectStarStatus}
                            renderProjectCard={renderProjectCard}
                            handleDragEnd={handleDragEnd}
                            isModalOpen={isModalOpen}
                            setIsModalOpen={setIsModalOpen}
                            user={user}
                        />
                    )}
                </MainContent>
                {isSidebarOpen && (
                    <DashboardSidebar
                        isSidebarOpen={isSidebarOpen}
                        setIsSidebarOpen={setIsSidebarOpen}
                        globalTodos={globalTodos}
                        handleGlobalTodoOperations={handleGlobalTodoOperations}
                    />
                )}
            </MainWrapper>
            {isModalOpen && (
                <AddProjectModal onClose={() => setIsModalOpen(false)} />
            )}
            {createWorkspaceModalOpen && (
                <CreateWorkspaceModal onClose={() => setCreateWorkspaceModalOpen(false)} />
            )}
            {isChecklistModalOpen && selectedProject && (
                <ProjectChecklistModal project={selectedProject} onClose={() => setIsChecklistModalOpen(false)} />
            )}
            {showDetailsModal && (
                <ProjectDetailsModal project={showDetailsModal} onClose={() => setShowDetailsModal(null)} />
            )}
            {isAddBookmarkModalOpen && (
                <AddBookmarkModal isOpen={isAddBookmarkModalOpen} onClose={() => setIsAddBookmarkModalOpen(false)} userId={user.uid} />
            )}
            {isAddSaveModalOpen && (
                <AddSaveModal isOpen={isAddSaveModalOpen} onClose={() => setIsAddSaveModalOpen(false)} userId={user.uid} allUniqueTags={allUniqueTags} addSave={addSave} allSaves={allSaves} />
            )}
            {isThinkingModalOpen && (
                <ThinkingItemsModal
                    onClose={() => {
                        // Navigating to '/' is handled by the modal itself.
                        // The useEffect in Dashboard handles setIsThinkingModalOpen(false) based on path change.
                        // This prop could be used for other cleanup if needed, but for now, it's just a placeholder
                        // or can be removed if ThinkingItemsModal doesn't expect any specific function for other purposes.
                        // For now, let's ensure it navigates away if it was closed by other means not yet considered,
                        // though current logic in modal already does this.
                        // To be safe and ensure URL consistency if modal were somehow closed without navigating:
                        if (location.pathname === '/ideas') {
                            navigate('/');
                        }
                    }}
                />
            )}
            {showDetailsModal && (
                <ProjectDetailsModal project={showDetailsModal} onClose={() => setShowDetailsModal(null)} />
            )}
            <FullscreenButton>
                <FullscreenIcon onClick={toggleFullscreen} />
            </FullscreenButton>
        </DashboardContainer>
    );
}

export default Dashboard;
